import * as babylon from "@babylonjs/core";

import { BabylonState } from "../../state";

/**
 * Syncs the data-space position of all entities with the current time.
 *
 * TODO(dave): this is currently logarithmic complexity for all entities.
 * Decouple it from the render loop.
 * Using low/high LODs will also set a cap on the number of points that
 * need to be inspected.
 */
export function updatePositions(state: BabylonState) {
  const time = state.sceneConfig.time;
  if (time === undefined) {
    return;
  }
  for (const entity of Object.values(state.entities)) {
    if (!entity.position || entity.position.cache.load.state !== "loaded") {
      continue;
    }
    const cache = entity.position.cache;
    // Figure out what index from the position cache to load.
    // This could possibly be implemented with an amortized linear search instead
    // by using the currently-loaded logtime, but that only
    // works at slow playback speeds, and doesn't work for fast
    // playback or seeking.
    // The logtime binary search will also be reusable for buffering data
    // when we decide to downsample the full path followed by the Entity
    // eg we can downsample to 1hz or so for the full path, and then buffer playback
    // for a few seconds based on the render framerate (60fps / playbackSpeed)
    const sample = sampleCacheAtTime(cache, time);
    if (!sample) {
      continue;
    }
    entity.position.current.logtime = sample.logtime;
    entity.position.current.vector = sample.vector;
  }
}

/** A position cache: parallel arrays of sample times and points. */
export interface SampledCache {
  logtimes: bigint[];
  allPoints: babylon.Vector3[];
}

/**
 * The cached position at `time`, interpolating between the bracketing samples.
 *
 * Clamps outside the cache's range: before the first sample yields the first
 * point, after the last yields the last.
 *
 * **Returns the cache's own vector on an exact sample hit**, rather than a copy —
 * the historical behavior, kept because it avoids an allocation on the
 * 60fps path. Callers must therefore treat the result as read-only, or use
 * {@link localPositionAt} which always copies.
 */
function sampleCacheAtTime(
  cache: SampledCache,
  time: bigint,
): { logtime: bigint; vector: babylon.Vector3 } | undefined {
  const index = logTimeBinarySearch(cache.logtimes, time);
  const lastTime = cache.logtimes.at(index);
  const lastPosition = cache.allPoints.at(index);
  if (lastTime === undefined || lastPosition === undefined) {
    return undefined;
  }
  const nextTime = cache.logtimes.at(index + 1);
  const nextPosition = cache.allPoints.at(index + 1);
  if (nextTime === undefined || nextPosition === undefined) {
    return { logtime: lastTime, vector: lastPosition };
  }
  const lerpAmount = Math.min(
    Math.max(Number(time - lastTime) / Number(nextTime - lastTime), 0),
    1,
  );
  return {
    logtime: time,
    vector: babylon.Vector3.Lerp(lastPosition, nextPosition, lerpAmount),
  };
}

/**
 * The position a `Position` component held at `time`, as a fresh vector.
 *
 * Shared by the live per-frame update and by the resampler that bakes a global
 * trajectory, so a trace provably passes through the mesh at every sample time
 * rather than approximating it with different interpolation.
 *
 * Always allocates, unlike the internal fast path: the resampler accumulates
 * into its result, and mutating a `cache.allPoints` element would rewrite the
 * very history it is reading.
 */
export function localPositionAt(
  cache: SampledCache,
  time: bigint,
): babylon.Vector3 | undefined {
  return sampleCacheAtTime(cache, time)?.vector.clone();
}

/**
 * Finds the closest index of the given log time array at or before the target logtime.
 *
 * If target comes before the first logtime, returns index 0.
 * If the target comes after the last logtime, returns the last index.
 */
export function logTimeBinarySearch(arr: bigint[], target: bigint): number {
  let left = 0;
  let right = arr.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midValue = arr[mid];

    if (midValue === target) {
      return mid; // Target found
    } else if (midValue < target) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return Math.max(0, left - 1); // Target not found exactly, return the index before the target.
}
